import { useEffect, useState } from 'react';
import { ComponentTypes } from '@dazn/lp-web/src/constants/componentTypes.constants';
import { contentfulService } from '@dazn/lp-web/src/services/contentful.service';
import { ContentfulTypes } from '@dazn/lp-web/src/constants/contentful.constants';
import {
    contentTiersProxy,
    competitionCarouselProxy,
    comingUpProxy,
    freemiumBannerProxy,
    footerProxy,
    bannersProxy,
    faqsProxy,
    subscriptionsRailProxy,
    standardRailProxy,
    spotlightRailProxy,
    videoPlayerProxy,
    showsRailProxy,
    benefitsProxy,
    countdownProxy,
    termsAndConditions,
    fullScreenBannerProxy,
    featuresV2,
    supportedDevices,
    redeemProxy,
    competitionDisplay,
    articlesRailProxy,
    umsPopupIntraction
} from '@dazn/lp-web/src/proxies';
import { getCountry } from '@dazn/lp-web/src/utils/dazn/Region';
import { deviceGraphicProxy } from '@dazn/lp-web/src/proxies/devicegraphic.proxy';
import { getPageId } from '@dazn/lp-web/src/utils/pageUtils';
import { usePageChange } from '@dazn/lp-web/src/hooks/usePageChange';
import {
    getVariationFromContentful,
    getVariationValue
} from '@dazn/lp-web/src/features/getVariationFromContentful';
import { runBoxingBetaAuth } from '../utils/boxingBeta';
import { isProdEnvironment } from '@dazn/lp-web/src/utils/DevTools';
import { heroBannerCenterAlignedProxy } from '../proxies/heroBannerCenterAligned.proxy';
import { marketPropositionproxy } from '../proxies/marketProposition.proxy';
import { daznBetproxy } from '../proxies/daznbet.proxy';

interface UsePageData {
    data: any;
    isLoading: boolean;
    error: unknown;
    pageError?: string;
    hideExploreButton: boolean;
    hideHeader: boolean;
}

export function usePageData(): UsePageData {
    const [data, setData] = useState<any>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [hideExploreButton, setHideExploreButton] = useState(false);
    const [hideHeader, setHideHeader] = useState(false);
    const [error, setError] = useState<unknown>(null);
    const [pageError, setPageError] = useState<string | undefined>();

    const { pathname } = usePageChange();

    useEffect(() => {
        function transform(response: any): any {
            const components: any = response?.items?.[0]?.fields?.components;

            return components
                ?.map((component: any) => {
                    const componentType = component?.fields.componentType;

                    switch (componentType) {
                        case ComponentTypes.HEROBANNER_CENTER_ALIGNED:
                            return heroBannerCenterAlignedProxy(component);
                        case ComponentTypes.FULLSCREEN_BANNER:
                            return fullScreenBannerProxy(component);
                        case ComponentTypes.CONTENT_TIERS:
                            return contentTiersProxy(component);
                        case ComponentTypes.STANDARD_RAIL:
                            return standardRailProxy(component);
                        case ComponentTypes.SHOWS_RAIL:
                            return showsRailProxy(component);
                        case ComponentTypes.SPOTLIGHT_RAIL:
                            return spotlightRailProxy(component);
                        case ComponentTypes.BANNERS:
                            return bannersProxy(component);
                        case ComponentTypes.BENEFITS:
                            return benefitsProxy(component);
                        case ComponentTypes.COMING_UP_RAIL:
                            return comingUpProxy(component);
                        case ComponentTypes.SUBSCRIPTIONS_RAIL:
                            return subscriptionsRailProxy(component);
                        case ComponentTypes.ARTICLES_RAIL:
                            return articlesRailProxy(component);
                        case ComponentTypes.COMPETITION_CAROUSEL:
                            return competitionCarouselProxy(component);
                        case ComponentTypes.FREEMIUM_BANNER:
                            return freemiumBannerProxy(component);
                        case ComponentTypes.FAQS:
                            return faqsProxy(component);
                        case ComponentTypes.FOOTER:
                            return footerProxy(component);
                        case ComponentTypes.VIDEO_PLAYER:
                            return videoPlayerProxy(component);
                        case ComponentTypes.DEVICEGRAPHIC:
                            return deviceGraphicProxy(component);
                        case ComponentTypes.COUNTDOWN:
                            return countdownProxy(component);
                        case ComponentTypes.TERMSANDCONDITIONS:
                            return termsAndConditions(component);
                        case ComponentTypes.FEATURES_V2:
                            return featuresV2(component);
                        case ComponentTypes.SUPPORTED_DEVICES:
                            return supportedDevices(component);
                        case ComponentTypes.REDEEM_PAGE:
                            return redeemProxy(component);
                        case ComponentTypes.COMPETITION_DISPLAY:
                            return competitionDisplay(component);
                        case ComponentTypes.MARKET_PROPOSITION:
                            return marketPropositionproxy(component);
                        case ComponentTypes.DAZN_BET:
                            return daznBetproxy(component);
                        case ComponentTypes.UMS_POPUP_INTRACTION:
                            return umsPopupIntraction(component);
                        default:
                    }
                })
                .filter((data: any) => data);
        }

        async function getPageData() {
            try {
                const params: { [key: string]: string } = {
                    'fields.pages[in]': getPageId(),
                    'fields.includedCountries[in]': `${getCountry().toUpperCase()},ALL`,
                    'fields.excludedCountries[nin]': getCountry().toUpperCase(),
                    'fields.environment[in]': isProdEnvironment
                        ? 'Live'
                        : 'Test'
                };

                if (
                    getVariationFromContentful &&
                    getVariationValue !== undefined
                ) {
                    params['fields.variations[in]'] = getVariationValue;
                }

                const response: any = await contentfulService({
                    contentType: ContentfulTypes.LP_ROOT_CONFIG,
                    params: params
                });
                const contentfulData = transform(response);

                await runBoxingBetaAuth(response, setPageError);

                setHideExploreButton(
                    response?.items?.[0]?.fields?.pages?.includes(
                        'hideExploreButton'
                    )
                );
                setHideHeader(
                    response?.items?.[0]?.fields?.components[0]?.fields
                        ?.componentType === ComponentTypes.UMS_POPUP_INTRACTION
                );
                setData(contentfulData);
            } catch (err) {
                console.log({ err });
                setError(err);
                setData(null);
            } finally {
                setIsLoading(false);
            }
        }

        getPageData();
    }, [pathname]);

    return {
        data,
        isLoading,
        error,
        pageError,
        hideExploreButton,
        hideHeader
    };
}
